"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { HomepageAssets } from "@/app/components/icm 3.0/homepage-assets";
import { cn } from "@/lib/utils";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";

const APPLE_APP_URL = process.env.ICM_APPLE_APP_URL || "#";
const PLAYSTORE_URL = process.env.ICM_PLAYSTORE_URL || "#";

/**
 * Fixed bottom-right “Scan to Download” chip.
 * Collapsed by default; expands on hover and on click (pinned until click-outside).
 */
const FloatingAppDownload = () => {
    const [expanded, setExpanded] = useState(false);
    const [pinned, setPinned] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearLeaveTimer = () => {
        if (leaveTimerRef.current) {
            clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
    };

    useEffect(() => {
        return () => clearLeaveTimer();
    }, []);

    useEffect(() => {
        if (!pinned) return;

        const onPointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setPinned(false);
                setExpanded(false);
            }
        };

        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [pinned]);

    const expand = () => {
        clearLeaveTimer();
        setExpanded(true);
    };

    const collapseIfUnpinned = () => {
        if (pinned) return;
        clearLeaveTimer();
        // Ignore spurious leave events from inner layout while the card is resizing.
        leaveTimerRef.current = setTimeout(() => {
            setExpanded(false);
            leaveTimerRef.current = null;
        }, 120);
    };

    const pinOpen = () => {
        clearLeaveTimer();
        setPinned(true);
        setExpanded(true);
    };

    return (
        <div
            ref={rootRef}
            className="pointer-events-none fixed bottom-5 right-5 z-[110] hidden md:block md:bottom-6 md:right-6"
        >
            <div
                className={cn(
                    "pointer-events-auto relative overflow-hidden bg-[#262626] text-white shadow-[0_4px_15px_0_rgba(0,0,0,0.15)] motion-reduce:transition-none",
                    "box-border transition-[width,height,border-radius,padding] duration-300 ease-out",
                    expanded
                        ? "h-[163px] w-[min(367px,calc(100vw-2.5rem))] rounded-2xl p-[11px]"
                        : "h-[66px] w-[152px] cursor-pointer rounded-[var(--radius-m)] p-2",
                )}
                onPointerEnter={expand}
                onPointerLeave={collapseIfUnpinned}
                onClick={pinOpen}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (expanded && pinned) {
                            setPinned(false);
                            setExpanded(false);
                        } else {
                            pinOpen();
                        }
                    }
                    if (event.key === "Escape") {
                        setPinned(false);
                        setExpanded(false);
                    }
                }}
                role={expanded ? undefined : "button"}
                tabIndex={expanded ? undefined : 0}
                aria-expanded={expanded}
                aria-label="Scan to download the InCred Money app"
            >
                <div
                    className={cn(
                        "flex h-full",
                        expanded ? "items-start gap-4" : "items-center gap-2",
                    )}
                >
                    <div
                        className={cn(
                            "relative shrink-0 overflow-hidden motion-reduce:transition-none",
                            "transition-[width,height,border-radius] duration-300 ease-out",
                            expanded ? "size-[141px] rounded-lg" : "size-[50px] rounded",
                        )}
                    >
                        <Image
                            src={HomepageAssets.qrCode}
                            alt=""
                            fill
                            sizes="141px"
                            className="object-cover"
                        />
                    </div>

                    <div className="relative min-h-0 min-w-0 flex-1 self-stretch">
                        <p
                            className={cn(
                                "flex h-full items-center text-base font-semibold leading-[22px]",
                                "transition-opacity duration-150 ease-out",
                                expanded ? "pointer-events-none opacity-0" : "opacity-100",
                            )}
                            aria-hidden={expanded}
                        >
                            Scan to
                            <br />
                            Download
                        </p>

                        <div
                            className={cn(
                                "absolute left-0 top-0 flex w-[188px] flex-col items-start gap-5 pt-[3px]",
                                "transition-opacity duration-200 ease-out",
                                expanded
                                    ? "opacity-100 delay-75"
                                    : "pointer-events-none opacity-0",
                            )}
                            aria-hidden={!expanded}
                            inert={!expanded}
                        >
                            <p className="h-11 w-full text-base font-semibold leading-[22px]">
                                Access every opportunity in one app!
                            </p>
                            <div className="flex w-full flex-col items-start gap-2">
                                <p className="text-sm leading-5 text-white/75">
                                    <span className="font-regular">Download </span>
                                    <span className="font-semibold">InCred Money</span>
                                </p>
                                <div className="flex w-[102px] items-center justify-between">
                                    <a
                                        href={APPLE_APP_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        tabIndex={expanded ? 0 : -1}
                                        aria-label="Download on the App Store"
                                        className="size-8 shrink-0 overflow-hidden rounded-[7px] transition-opacity hover:opacity-80"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setAnalyticsEvent("Floating_App_Download", "App_Store");
                                        }}
                                    >
                                        <Image
                                            src={HomepageAssets.floatingAppStore}
                                            alt=""
                                            width={32}
                                            height={32}
                                            className="size-8 object-cover"
                                        />
                                    </a>
                                    <span
                                        className="h-[33px] w-px shrink-0 bg-white"
                                        aria-hidden
                                    />
                                    <a
                                        href={PLAYSTORE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        tabIndex={expanded ? 0 : -1}
                                        aria-label="Get it on Google Play"
                                        className="flex size-8 shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-80"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setAnalyticsEvent("Floating_App_Download", "Play_Store");
                                        }}
                                    >
                                        <Image
                                            src={HomepageAssets.floatingPlayStore}
                                            alt=""
                                            width={32}
                                            height={32}
                                            className="size-8 object-contain"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FloatingAppDownload;
